/**
 * MinLOS (Minimum Length of Stay) Service
 *
 * Validates stay length against MinLOS rules and handles gap-fill overrides.
 */

import { supabase } from './supabaseClient';
import { gapDetectionService } from './gapDetectionService';
import {
  normalizeMinLosOverrides,
  resolveEffectiveMinLos,
} from './minLosResolver';
import type {
  MinLosGapOverride,
  MinLosLastMinuteOverride,
} from '@/types/revenue';

export interface MinLosRule {
  id: string;
  period: 'daily' | 'weekly' | 'monthly' | 'seasonal' | 'specific_dates';
  dateRange?: { start: Date; end: Date };
  dayOfWeek?: number[];
  minLos: number;
  allowGapFillOverride: boolean;
  gapOverride?: MinLosGapOverride;
  lastMinuteOverride?: MinLosLastMinuteOverride;
  priority: number;
  active: boolean;
  roomTypeIds?: string[];
}

/** Whole days from local midnight today to the local midnight of `date`. */
function daysFromToday(date: Date): number {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  return Math.round(
    (target.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24)
  );
}

export interface MinLosValidationResult {
  isValid: boolean;
  requiredMinLos: number;
  actualNights: number;
  gapFillOverrideAllowed: boolean;
  gapFillOverrideApplied: boolean;
  overrideReason?: string;
  gapId?: string;
  applicableRule?: MinLosRule;
}

export class MinLosService {
  /**
   * Get the applicable MinLOS for a specific property, room type, and check-in date
   */
  async getApplicableMinLos(
    propertyId: string,
    roomTypeId: string,
    checkInDate: Date
  ): Promise<{
    minLos: number;
    rule?: MinLosRule;
    allowGapFillOverride: boolean;
    gapOverride?: MinLosGapOverride;
    lastMinuteOverride?: MinLosLastMinuteOverride;
  }> {
    try {
      const rules = await this.getMinLosRules(propertyId, roomTypeId);

      if (rules.length === 0) {
        return { minLos: 1, allowGapFillOverride: false };
      }

      // Find the most specific applicable rule
      const applicableRule = this.findApplicableRule(
        rules,
        checkInDate,
        roomTypeId
      );

      if (!applicableRule) {
        return { minLos: 1, allowGapFillOverride: false };
      }

      return {
        minLos: applicableRule.minLos,
        rule: applicableRule,
        allowGapFillOverride: applicableRule.allowGapFillOverride,
        gapOverride: applicableRule.gapOverride,
        lastMinuteOverride: applicableRule.lastMinuteOverride,
      };
    } catch (error) {
      console.error('Error getting applicable MinLOS:', error);
      return { minLos: 1, allowGapFillOverride: false };
    }
  }

  /**
   * Validate if a stay meets MinLOS requirements
   */
  async validateStayLength(
    propertyId: string,
    roomTypeId: string,
    checkIn: Date,
    checkOut: Date
  ): Promise<MinLosValidationResult> {
    const nights = Math.floor(
      (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (nights <= 0) {
      return {
        isValid: false,
        requiredMinLos: 1,
        actualNights: nights,
        gapFillOverrideAllowed: false,
        gapFillOverrideApplied: false,
        overrideReason: 'Invalid stay duration',
      };
    }

    // Get applicable MinLOS rule + its override configs
    const {
      minLos,
      rule,
      allowGapFillOverride,
      gapOverride,
      lastMinuteOverride,
    } = await this.getApplicableMinLos(propertyId, roomTypeId, checkIn);

    // Only hit the DB for an enclosing gap when a gap override could apply.
    let enclosingGap: { id: string; durationNights: number } | null = null;
    if (gapOverride?.enabled) {
      enclosingGap = await gapDetectionService.getEnclosingGap(
        propertyId,
        roomTypeId,
        checkIn,
        checkOut
      );
    }

    const resolution = resolveEffectiveMinLos({
      baseMinLos: minLos,
      gapOverride,
      gapNights: enclosingGap?.durationNights,
      lastMinuteOverride,
      daysAhead: daysFromToday(checkIn),
    });

    const overrideApplied = resolution.appliedOverride !== 'none';

    if (nights >= resolution.effectiveMinLos) {
      return {
        isValid: true,
        requiredMinLos: resolution.effectiveMinLos,
        actualNights: nights,
        gapFillOverrideAllowed: allowGapFillOverride || !!gapOverride?.enabled,
        gapFillOverrideApplied: resolution.appliedOverride === 'gap',
        overrideReason: overrideApplied
          ? `MinLOS relaxed from ${minLos} to ${resolution.effectiveMinLos} (${resolution.appliedOverride} override)`
          : undefined,
        gapId:
          resolution.appliedOverride === 'gap' ? enclosingGap?.id : undefined,
        applicableRule: rule,
      };
    }

    // Stay still doesn't meet the (possibly relaxed) MinLOS.
    return {
      isValid: false,
      requiredMinLos: resolution.effectiveMinLos,
      actualNights: nights,
      gapFillOverrideAllowed: allowGapFillOverride || !!gapOverride?.enabled,
      gapFillOverrideApplied: false,
      overrideReason: `Minimum stay of ${resolution.effectiveMinLos} night${resolution.effectiveMinLos > 1 ? 's' : ''} required. Selected stay is only ${nights} night${nights > 1 ? 's' : ''}.`,
      applicableRule: rule,
    };
  }

  /**
   * Get all MinLOS rules for a property
   */
  private async getMinLosRules(
    propertyId: string,
    _roomTypeId?: string
  ): Promise<MinLosRule[]> {
    try {
      const { data, error } = await supabase
        .from('pricing_rules')
        .select('*')
        .eq('property_id', propertyId)
        .eq('rule_type', 'min_los')
        .eq('active', true)
        .order('priority', { ascending: false });

      if (error || !data) {
        return [];
      }

      // Transform database records to MinLosRule format
      return data.map((rule) => ({
        id: rule.id,
        period: (rule.conditions?.period || 'daily') as MinLosRule['period'],
        dateRange:
          rule.conditions?.date_range?.start_date &&
          rule.conditions?.date_range?.end_date
            ? {
                start: new Date(rule.conditions.date_range.start_date),
                end: new Date(rule.conditions.date_range.end_date),
              }
            : undefined,
        dayOfWeek: rule.conditions?.day_of_week,
        minLos: rule.conditions?.min_los || 1,
        allowGapFillOverride: rule.conditions?.allow_gap_fill_override || false,
        ...normalizeMinLosOverrides(rule.conditions),
        priority: rule.priority || 0,
        active: rule.active,
        roomTypeIds: rule.room_type_ids || undefined,
      }));
    } catch (error) {
      console.error('Error loading MinLOS rules:', error);
      return [];
    }
  }

  /**
   * Find the most applicable rule for a given date and room type
   */
  private findApplicableRule(
    rules: MinLosRule[],
    checkInDate: Date,
    roomTypeId: string
  ): MinLosRule | undefined {
    const dayOfWeek = checkInDate.getDay();

    // Filter rules that apply to this date and room type
    const applicableRules = rules.filter((rule) => {
      // Check room type scope
      if (rule.roomTypeIds && rule.roomTypeIds.length > 0) {
        if (!rule.roomTypeIds.includes(roomTypeId)) {
          return false;
        }
      }

      // Check date range for seasonal/specific date rules
      if (rule.dateRange) {
        if (
          checkInDate < rule.dateRange.start ||
          checkInDate > rule.dateRange.end
        ) {
          return false;
        }
      }

      // Check day of week for weekly rules
      if (rule.period === 'weekly' && rule.dayOfWeek) {
        if (!rule.dayOfWeek.includes(dayOfWeek)) {
          return false;
        }
      }

      return true;
    });

    if (applicableRules.length === 0) {
      return undefined;
    }

    // Sort by specificity (most specific first):
    // 1. Room-type specific rules
    // 2. Date-range specific rules
    // 3. Day-of-week specific rules
    // 4. Higher priority
    applicableRules.sort((a, b) => {
      // Room-type specific vs global
      const aRoomSpecific = a.roomTypeIds && a.roomTypeIds.length > 0 ? 1 : 0;
      const bRoomSpecific = b.roomTypeIds && b.roomTypeIds.length > 0 ? 1 : 0;
      if (aRoomSpecific !== bRoomSpecific) {
        return bRoomSpecific - aRoomSpecific;
      }

      // Date range specific
      const aDateSpecific = a.dateRange ? 1 : 0;
      const bDateSpecific = b.dateRange ? 1 : 0;
      if (aDateSpecific !== bDateSpecific) {
        return bDateSpecific - aDateSpecific;
      }

      // Day of week specific
      const aDowSpecific = a.dayOfWeek ? 1 : 0;
      const bDowSpecific = b.dayOfWeek ? 1 : 0;
      if (aDowSpecific !== bDowSpecific) {
        return bDowSpecific - aDowSpecific;
      }

      // Higher priority wins
      return b.priority - a.priority;
    });

    return applicableRules[0];
  }

  /**
   * Mark a gap as filled when MinLOS override is used
   */
  async markGapFilledOnOverride(
    gapId: string,
    reservationId: string,
    rate: number
  ): Promise<void> {
    await gapDetectionService.markGapFilled(gapId, reservationId, rate);
  }
}

// Export singleton instance
export const minLosService = new MinLosService();
